import {
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    Dimensions,
    Platform
} from 'react-native';
import EStyleSheet from 'react-native-extended-stylesheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import {
    addProviderError,
    batchChangeConsent
} from '../../actions/consents';
import { getConsentData } from '../../agreementsConfig';
import {
    Button,
    ButtonsGroup,
    LightProfileSelector,
    LoadingView,
    Modal,
    Page,
    Section,
    Title,
    TopNotification,
    View
} from '../../common';
import {
    CHILD_CLINIC_HISTORY_KEY,
    CHILD_HISTORY_KEY,
    FEEDBACK_AUTHOR_NAME,
    ONLINE_BOOKING_CHILD_CLINIC,
    ONLINE_BOOKING_CLINIC,
    STORE_PROFILE_CHILD,
    usePersonalData,
    USER_CLINIC_HISTORY_KEY,
    USER_HISTORY_KEY
} from '../../hooks/usePersonalData';
import { syncUserState } from '../../init';
import { getUserLoadingState } from '../../selectors';
import {
    filterProfileConsentsByCards,
    getProviders
} from '../../selectors/consents';
import { getProviderCapabilities } from '../../selectors/providerCodes';
import { openURL } from '../../services/device.js';
import userServices from '../../services/user.js';
import { capitalize } from '../../utils';
import {
    getClinicsConsents,
    initializeClinicsConsentsState
} from '../../utils/consentsUtils';
import { getSupportBody } from '../../utils/supportInfo.js';
import { ClinicConsentsGroup } from '../ConsentConfirmation/ClinicConsentsGroup';
import {
    ConsentRetractionModal,
    shouldShowDocumentsConsentsRetractionModal
} from '../ConsentConfirmation/ConsentRetractionModal';
import { StyledAgreementCheckbox } from '../Visits/snippets/NewVisitModal/snippets.js';

const HEADER_HEIGHT = 88;
const TABS_HEIGHT = 48;

const shouldSyncUserStateForChildProfile = ({
    isChildProfile,
    onlineBookingClinicsConsents,
    initialState,
    onlineBookingClinicsState,
    historyClinicsState,
}) => {
    if (!isChildProfile) {
        return false;
    }

    return onlineBookingClinicsConsents.some((clinic) => {
        const providerCode = clinic?.providerCode;
        if (!providerCode) {
            return false;
        }

        const previousOnlineBooking = initialState?.onlineBookingClinics?.[providerCode] || false;
        const previousClinicHistory = initialState?.historyClinics?.[providerCode] || false;
        const nextOnlineBooking = onlineBookingClinicsState?.[providerCode] || false;
        const nextClinicHistory = historyClinicsState?.[providerCode] || false;

        const previousBothFalse = !previousOnlineBooking && !previousClinicHistory;
        const nextBothFalse = !nextOnlineBooking && !nextClinicHistory;

        return !previousBothFalse && nextBothFalse;
    });
};

export const DocumentsConsents = () => {
    const insets = useSafeAreaInsets();
    const [ selectedProfile, setSelectedProfile ] = useState(null);
    const {
        consents,
        dispatch,
        familyObject,
        isLoading,
        userInfo,
    } = usePersonalData();
    const isUserLoading = useSelector(getUserLoadingState);
    const providers = useSelector(getProviders);

    const providerCapabilities = useSelector(getProviderCapabilities);
    const [ error, setError ] = useState(null);

    const isChildProfile = selectedProfile && !selectedProfile?.isMe;
    const profileId = selectedProfile?.profileId || selectedProfile?.id;

    const cardsForSelectedProfile = useMemo(() => {
        if (!isChildProfile) {
            return userInfo?.me?.cards ?? [];
        }
        const relative = userInfo?.relatives?.find(
            (entry) => `${entry?.profileId ?? ''}` === `${profileId ?? ''}`,
        );
        return relative?.cards ?? relative?.profile?.cards ?? [];
    }, [ isChildProfile,
        profileId,
        userInfo ]);

    // Full consents from store; filter by cards only for this screen (see filterProfileConsentsByCards)
    const profileConsents = useMemo(() => {
        const raw = isChildProfile ? consents?.family?.[profileId] : consents?.user;
        if (!raw) {
            return raw;
        }
        return filterProfileConsentsByCards(raw, cardsForSelectedProfile);
    }, [ isChildProfile,
        profileId,
        consents,
        cardsForSelectedProfile ]);

    // Main profile consents
    const [ userHistoryChecked, setUserHistoryChecked ] = useState(false);
    const [ feedbackAuthorNameChecked, setFeedbackAuthorNameChecked ] = useState(false);

    // Child profile consents
    const [ storeProfileChildChecked, setStoreProfileChildChecked ] = useState(false);
    const [ childHistoryChecked, setChildHistoryChecked ] = useState(false);

    // ONLINE_BOOKING clinic consents
    const onlineBookingClinicsConsents = getClinicsConsents(
        profileConsents,
        isChildProfile ? ONLINE_BOOKING_CHILD_CLINIC : ONLINE_BOOKING_CLINIC,
        true,
        providerCapabilities,
    );
    const [ onlineBookingClinicsState, setOnlineBookingClinicsState ] = useState({});

    // History clinic consents
    const historyClinicsConsents = getClinicsConsents(
        profileConsents,
        isChildProfile ? CHILD_CLINIC_HISTORY_KEY : USER_CLINIC_HISTORY_KEY,
        true,
        providerCapabilities,
    );

    const [ historyClinicsState, setHistoryClinicsState ] = useState({});

    // Track initial state for detecting changes
    const [ initialState, setInitialState ] = useState(null);
    const [ offlineClinics, setOfflineClinics ] = useState([]);

    // Initialize states when profile changes
    useEffect(() => {
        if (!profileConsents) {
            return;
        }

        if (isChildProfile) {
            const storeValue = profileConsents?.main?.[STORE_PROFILE_CHILD];
            const historyValue = profileConsents?.main?.[CHILD_HISTORY_KEY];

            setStoreProfileChildChecked(storeValue === true);
            setChildHistoryChecked(historyValue === true);
        } else {
            const historyValue = profileConsents?.main?.[USER_HISTORY_KEY];
            const feedbackValue = profileConsents?.main?.[FEEDBACK_AUTHOR_NAME];

            setUserHistoryChecked(historyValue === true);
            setFeedbackAuthorNameChecked(feedbackValue === true);
        }

        // Initialize clinic consents
        const onlineBookingClinics = getClinicsConsents(
            profileConsents,
            isChildProfile ? ONLINE_BOOKING_CHILD_CLINIC : ONLINE_BOOKING_CLINIC,
            false,
            providerCapabilities,
        );
        const historyClinics = getClinicsConsents(
            profileConsents,
            isChildProfile ? CHILD_CLINIC_HISTORY_KEY : USER_CLINIC_HISTORY_KEY,
            false,
            providerCapabilities,
        );

        const onlineBookingState = initializeClinicsConsentsState(onlineBookingClinics);
        const historyState = initializeClinicsConsentsState(historyClinics);

        setOnlineBookingClinicsState(onlineBookingState);
        setHistoryClinicsState(historyState);

        // Save initial state for change detection
        setInitialState({
            isChildProfile,
            storeProfileChild: isChildProfile ? (profileConsents?.main?.[STORE_PROFILE_CHILD] === true) : null,
            childHistory: isChildProfile ? (profileConsents?.main?.[CHILD_HISTORY_KEY] === true) : null,
            userHistory: !isChildProfile ? (profileConsents?.main?.[USER_HISTORY_KEY] === true) : null,
            feedbackAuthorName: !isChildProfile ? (profileConsents?.main?.[FEEDBACK_AUTHOR_NAME] === true) : null,
            onlineBookingClinics: { ...onlineBookingState },
            historyClinics: { ...historyState },
        });
    }, [ selectedProfile,
        isChildProfile,
        profileConsents,
        providerCapabilities ]);

    // Check if there are any changes
    const hasChanges = () => {
        if (!initialState) {
            return false;
        }

        if (isChildProfile) {
            if (storeProfileChildChecked !== initialState.storeProfileChild) {
                return true;
            }
            if (childHistoryChecked !== initialState.childHistory) {
                return true;
            }
        } else {
            if (userHistoryChecked !== initialState.userHistory) {
                return true;
            }
            if (feedbackAuthorNameChecked !== initialState.feedbackAuthorName) {
                return true;
            }
        }

        // Check online booking clinics changes
        for (const key in onlineBookingClinicsState) {
            if (onlineBookingClinicsState[key] !== initialState.onlineBookingClinics[key]) {
                return true;
            }
        }

        // Check history clinics changes
        for (const key in historyClinicsState) {
            if (historyClinicsState[key] !== initialState.historyClinics[key]) {
                return true;
            }
        }

        return false;
    };

    // Handler for online booking clinic consent change
    const handleOnlineBookingClinicChange = (providerCode, newValue) => {
        setOnlineBookingClinicsState((prevState) => ({
            ...prevState,
            [providerCode]: newValue,
        }));
    };

    // Handler for history clinic consent change
    const handleHistoryClinicChange = (providerCode, newValue) => {
        setHistoryClinicsState((prevState) => ({
            ...prevState,
            [providerCode]: newValue,
        }));
    };

    // Reset to initial state
    const handleCancel = () => {
        if (!initialState) {
            return;
        }

        if (isChildProfile) {
            setStoreProfileChildChecked(initialState.storeProfileChild);
            setChildHistoryChecked(initialState.childHistory);
        } else {
            setUserHistoryChecked(initialState.userHistory);
            setFeedbackAuthorNameChecked(initialState.feedbackAuthorName);
        }

        setOnlineBookingClinicsState({ ...initialState.onlineBookingClinics });
        setHistoryClinicsState({ ...initialState.historyClinics });

        setError(null);
    };

    // Handler for profile selection with unsaved changes check
    const handleProfileSelect = (newProfile) => {
        // If no changes, just switch profile
        if (!hasChanges()) {
            setSelectedProfile(newProfile);
            return;
        }

        // If there are unsaved changes, show confirmation modal
        setTimeout(() => Page.showModal(
            <Modal hideClose>
                <Title
                    style={{
                        alignSelf: 'center',
                        textAlign: 'center',
                    }}
                    bold
                    id="titles.attention"
                />
                <Title
                    numberOfLines={null}
                    style={{
                        marginTop: 12,
                        marginHorizontal: 16,
                        textAlign: 'center',
                    }}
                >
                    {Object.R('titles.unsavedChangesWillBeLost', { profile: selectedProfile?.fullNameRu || selectedProfile?.originalName || '' })}
                </Title>
                <ButtonsGroup style={{ marginTop: 20 }}>
                    <Button
                        transparent
                        title="buttons.cancel"
                        action={Page.closeModal}
                    />
                    <Button
                        primary
                        title="buttons.OK"
                        action={() => {
                            Page.closeModal();
                            // Reset changes and switch profile
                            handleCancel();
                            setSelectedProfile(newProfile);
                        }}
                    />
                </ButtonsGroup>
            </Modal>,
        ), 100);
    };

    // Save changes
    const handleSave = () => {
        const mainConsents = profileConsents?.main || {};

        // Use new function to check if modal should be shown and which blocks
        const modalInfo = shouldShowDocumentsConsentsRetractionModal({
            isChildProfile,
            profileConsents,
            mainConsents,
            userHistoryChecked,
            feedbackAuthorNameChecked,
            storeProfileChildChecked,
            childHistoryChecked,
            historyClinicsState,
            userClinicHistoryKey: USER_CLINIC_HISTORY_KEY,
            childClinicHistoryKey: CHILD_CLINIC_HISTORY_KEY,
        });

        if (!modalInfo.shouldShow) {
            // If modal shouldn't be shown, proceed directly to consent update
            handleConsentUpdate();
            return;
        }

        // Display confirmation modal with appropriate blocks
        const profileData = isChildProfile
            ? familyObject?.[profileId]
            : selectedProfile;

        const profileForModal = {
            name: profileData?.fullNameRu || profileData?.originalName || profileData?.name || '',
            birthday: profileData?.birthday || '',
        };

        Page.showModal(
            <ConsentRetractionModal
                profile={profileForModal}
                uncheckedClinics={modalInfo.uncheckedClinics}
                showMedicalDataHistoryBlock={modalInfo.showMedicalDataHistoryBlock}
                showClinicBlock={modalInfo.showClinicBlock}
                showChildStorageBlock={modalInfo.showChildStorageBlock}
                onConfirm={() => {
                    Page.closeModal();
                    handleConsentUpdate();
                }}
                onCancel={Page.closeModal}
            />,
        );
    };

    const handleConsentUpdate = () => {
        const changedConsents = {};
        const batchConsents = [];
        const shouldSyncUserState = shouldSyncUserStateForChildProfile({
            isChildProfile,
            onlineBookingClinicsConsents,
            initialState,
            onlineBookingClinicsState,
            historyClinicsState,
        });

        const clinicConsents = {};
        if (isChildProfile) {
            // Child profile consents - save only changed consents
            if (storeProfileChildChecked !== initialState.storeProfileChild) {
                changedConsents[STORE_PROFILE_CHILD] = storeProfileChildChecked;
                batchConsents.push({type: STORE_PROFILE_CHILD, provided: storeProfileChildChecked});
                if (!storeProfileChildChecked) {
                    console.log('No STORE_PROFILE_CHILD: user will be deleted: ', profileId);
                    userServices.deleteChildSimplified(profileId);
                    setSelectedProfile('');
                }
            }
            if (childHistoryChecked !== initialState.childHistory) {
                changedConsents[CHILD_HISTORY_KEY] = childHistoryChecked;
                batchConsents.push({type: CHILD_HISTORY_KEY, provided: childHistoryChecked});
            }

            // Online booking clinic consents - save only changed
            onlineBookingClinicsConsents.forEach((clinic) => {
                const oldValue = initialState.onlineBookingClinics[clinic.providerCode];
                const newValue = onlineBookingClinicsState[clinic.providerCode] || false;
                if (oldValue !== newValue) {
                    const consentKey = `${ONLINE_BOOKING_CHILD_CLINIC}::${clinic.providerCode}`;
                    changedConsents[consentKey] = newValue;
                    if (!clinicConsents[clinic?.providerCode]) {
                        clinicConsents[clinic?.providerCode] = {};
                    }
                    clinicConsents[clinic?.providerCode][ONLINE_BOOKING_CHILD_CLINIC] = newValue;
                    batchConsents.push({type: ONLINE_BOOKING_CHILD_CLINIC, provided: newValue, providerCode: clinic?.providerCode});
                }
            });

            // History clinic consents - save only changed
            historyClinicsConsents.forEach((clinic) => {
                const oldValue = initialState.historyClinics[clinic.providerCode];
                const newValue = historyClinicsState[clinic.providerCode] || false;
                if (oldValue !== newValue) {
                    const consentKey = `${CHILD_CLINIC_HISTORY_KEY}::${clinic.providerCode}`;
                    changedConsents[consentKey] = newValue;
                    if (!clinicConsents[clinic?.providerCode]) {
                        clinicConsents[clinic?.providerCode] = {};
                    }
                    clinicConsents[clinic?.providerCode][CHILD_CLINIC_HISTORY_KEY] = newValue;
                    batchConsents.push({type: CHILD_CLINIC_HISTORY_KEY, provided: newValue, providerCode: clinic?.providerCode});
                }
            });
        } else {
            // Main profile consents - save only changed consents
            if (userHistoryChecked !== initialState.userHistory) {
                changedConsents[USER_HISTORY_KEY] = userHistoryChecked;
                batchConsents.push({type: USER_HISTORY_KEY, provided: userHistoryChecked});
            }
            if (feedbackAuthorNameChecked !== initialState.feedbackAuthorName) {
                changedConsents[FEEDBACK_AUTHOR_NAME] = feedbackAuthorNameChecked;
                batchConsents.push({type: FEEDBACK_AUTHOR_NAME, provided: feedbackAuthorNameChecked});
            }

            // Online booking clinic consents - save only changed
            onlineBookingClinicsConsents.forEach((clinic) => {
                const oldValue = initialState.onlineBookingClinics[clinic.providerCode];
                const newValue = onlineBookingClinicsState[clinic.providerCode] || false;
                if (oldValue !== newValue) {
                    const consentKey = `${ONLINE_BOOKING_CLINIC}::${clinic.providerCode}`;
                    changedConsents[consentKey] = newValue;
                    if (!clinicConsents[clinic?.providerCode]) {
                        clinicConsents[clinic?.providerCode] = {};
                    }
                    clinicConsents[clinic?.providerCode][ONLINE_BOOKING_CLINIC] = newValue;
                    batchConsents.push({type: ONLINE_BOOKING_CLINIC, provided: newValue, providerCode: clinic?.providerCode});
                }
            });

            // History clinic consents - save only changed
            historyClinicsConsents.forEach((clinic) => {
                const oldValue = initialState.historyClinics[clinic.providerCode];
                const newValue = historyClinicsState[clinic.providerCode] || false;
                if (oldValue !== newValue) {
                    const consentKey = `${USER_CLINIC_HISTORY_KEY}::${clinic.providerCode}`;
                    changedConsents[consentKey] = newValue;
                    if (!clinicConsents[clinic?.providerCode]) {
                        clinicConsents[clinic?.providerCode] = {};
                    }
                    clinicConsents[clinic?.providerCode][USER_CLINIC_HISTORY_KEY] = newValue;
                    batchConsents.push({type: USER_CLINIC_HISTORY_KEY, provided: newValue, providerCode: clinic?.providerCode});
                }
            });
        }

        console.log(`Changed consents ${profileId}`, changedConsents, batchConsents);

        const allClinics = Object.keys(clinicConsents);
        const offline = allClinics.filter(clinic => offlineClinics.includes(clinic));

        if (offline?.length) {
            dispatch(addProviderError(offline));
        }

        dispatch(batchChangeConsent({profileId, consents: batchConsents, onError: (e) => {
            try {
                setError({...e, level: 'error'});
            } catch {
                setError(e);
            }
        }, onComplete: (result) => {
            const errors = result?.filter?.(r => r.status === 'error') || [];
            setError(null);
            if (errors.length) {
                setOfflineClinics(Array.from(new Set(errors.map(e => e.providerCode))));
            }
            if (!errors.length && shouldSyncUserState) {
                syncUserState();
            }
        }}));

        // dispatch(changeConsent({
        //     consent: changedConsents,
        //     profileId: profileId,
        //     onError: (e) => {
        //         console.log('error: ', e);
        //         setError({
        //             ...e,
        //             level: 'error',
        //         });
        //
        //     },
        //     onComplete: () => {
        //         setError(null);
        //     },
        // }));
    };

    const clinics = useMemo(() => {
        const result = offlineClinics?.map?.(c => providers?.[c]?.name ?? c);
        return result?.join(', ') || '';
    }, [ offlineClinics, providers ]);

    const clinicError = useMemo(() => {
        return offlineClinics?.length ? {
            level: 'error',
            message: capitalize(Object.R('errors.ErrorClinicConsent', {
                clinic: clinics,
            })),
        } : '';
    }, [ offlineClinics, clinics ]);



    const saveEnabled = hasChanges();
    const showOnlineBookingSection = onlineBookingClinicsConsents.some(clinic => clinic.shouldShow);
    const showHistorySection = historyClinicsConsents.some(clinic => clinic.shouldShow);

    // Reserve space for header, tabs (native), safe area - so content fits on screen
    const windowHeight = Dimensions.get('window').height;
    const minContentHeight = Platform.OS === 'web'
        ? windowHeight - 120
        : windowHeight - HEADER_HEIGHT - TABS_HEIGHT - insets.bottom;

    if (isLoading || isUserLoading) {
        return <LoadingView title={'hints.consentsLoading'}/>;
    }

    return (
        <View style={{ flex: 1, flexGrow: 1, minHeight: minContentHeight }}>
            <TopNotification
                hint={{
                    level: 'info',
                    message: 'hints.ConsentsRevokeHint',
                }}
                containerStyle={{ borderTopWidth: 1 }}/>

            <View style={{
                flex: 1,
                flexGrow: 1,
                justifyContent: 'space-between',
            }}>
                <Section
                    title="titles.ProfileSection"
                    style={{
                        marginBottom: 0,
                        marginTop: 8,
                    }}
                >
                    <LightProfileSelector
                        selectedProfile={selectedProfile}
                        onSelect={handleProfileSelect}
                    />
                    {!!selectedProfile && (
                        <View style={{
                            width: '100%',
                            paddingHorizontal: 10,
                            marginTop: 20,
                        }}>
                            {showOnlineBookingSection && (
                                <View style={{ marginTop: 0 }}>
                                    <Title
                                        numberOfLines={null}
                                        // style={{ marginBottom: 12 }}
                                    >
                                        {Object.R(isChildProfile
                                            ? 'titles.ChildConsentsRevokeMDTitle'
                                            : 'titles.ConsentsRevokeMDTitle',
                                        )}
                                    </Title>
                                    <ClinicConsentsGroup
                                        clinicsConsents={onlineBookingClinicsConsents}
                                        clinicsConsentsState={onlineBookingClinicsState}
                                        onClinicConsentChange={handleOnlineBookingClinicChange}
                                    />
                                </View>
                            )}

                            {showHistorySection && (
                                <View style={{ marginTop: 12 }}>
                                    <Title
                                        numberOfLines={null}
                                        // style={{ marginBottom: 12 }}
                                    >
                                        {Object.R(isChildProfile
                                            ? 'titles.ChildConsentsRevokeMDCTitle'
                                            : 'titles.consentsClinicSubtitle',
                                        )}
                                    </Title>
                                    <ClinicConsentsGroup
                                        clinicsConsents={historyClinicsConsents}
                                        clinicsConsentsState={historyClinicsState}
                                        onClinicConsentChange={handleHistoryClinicChange}
                                    />
                                </View>
                            )}
                            <View style={{ paddingTop: 16 }}>
                                {isChildProfile ? (
                                    <>
                                        <StyledAgreementCheckbox
                                            titleComponent={getConsentData(STORE_PROFILE_CHILD)?.TextData}
                                            selected={storeProfileChildChecked}
                                            onPress={() => setStoreProfileChildChecked(!storeProfileChildChecked)}
                                            containerStyle={{
                                                marginBottom: 0,
                                                marginTop: 0,
                                            }}
                                            checkboxStyle={styles.checkbox}
                                        />
                                        <StyledAgreementCheckbox
                                            titleComponent={getConsentData(CHILD_HISTORY_KEY)?.TextData}
                                            selected={childHistoryChecked}
                                            onPress={() => setChildHistoryChecked(!childHistoryChecked)}
                                            containerStyle={{
                                                marginBottom: 0,
                                                marginTop: 12,
                                            }}
                                            checkboxStyle={styles.checkbox}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <StyledAgreementCheckbox
                                            titleComponent={getConsentData(USER_HISTORY_KEY)?.TextData}
                                            selected={userHistoryChecked}
                                            onPress={() => setUserHistoryChecked(!userHistoryChecked)}
                                            containerStyle={{
                                                marginBottom: 0,
                                                marginTop: 0,
                                            }}
                                            checkboxStyle={styles.checkbox}
                                        />
                                        <StyledAgreementCheckbox
                                            titleComponent={getConsentData(FEEDBACK_AUTHOR_NAME)?.TextData}
                                            selected={feedbackAuthorNameChecked}
                                            onPress={() => setFeedbackAuthorNameChecked(!feedbackAuthorNameChecked)}
                                            containerStyle={{
                                                marginBottom: 0,
                                                marginTop: 12,
                                            }}
                                            checkboxStyle={styles.checkbox}
                                        />
                                    </>
                                )}
                            </View>
                        </View>
                    )}
                    {clinicError && (showHistorySection || showOnlineBookingSection) ? (<TopNotification
                        hint={clinicError}
                        containerStyle={{
                            borderBottomWidth: 0,
                            borderTopWidth: 1,
                            marginTop: 8,
                            marginBottom: 0,
                            paddingBottom: 0,
                        }}
                    />) : null}

                </Section>
                {error && (
                    <TopNotification
                        level="error"
                        hint={error}
                        containerStyle={{
                            borderBottomWidth: 0,
                            borderTopWidth: 1,
                            marginTop: 8,
                            marginBottom: 0,
                            paddingBottom: 0,
                        }}
                        submitText={Object.R('buttons.writeToSupport')}
                        submitContainerStyle={{
                            borderWidth: 0,
                            alignSelf: 'flex-end',
                            marginRight: 16,
                            marginBottom: 12,
                        }}
                        onSubmit={() => {
                            openURL(`mailto:${Object.R('supportEmail')}${getSupportBody()}`);
                        }}
                    />
                )}
                <ButtonsGroup style={{
                    paddingHorizontal: 24,
                    marginBottom: Math.max(12, insets.bottom),
                }}>
                    <Button
                        title="buttons.cancel"
                        action={handleCancel}
                        transparent
                    />
                    <Button
                        title="buttons.save"
                        action={handleSave}
                        primary
                        disabled={!saveEnabled}
                    />
                </ButtonsGroup>
            </View>
        </View>
    );
};

const styles = EStyleSheet.create({
    checkbox: {
        marginRight: 12,
        marginBottom: 12,
    },
});
